import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Container,
  Alert,
  CircularProgress,
  Snackbar,
  useTheme
} from '@mui/material';
import { useUser } from '../context/UserContext';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { fetchChatbotData, saveChatbotOptions } from '../services/api';
import SaveIcon from '@mui/icons-material/Save';

function AdminOptions() {
  const theme = useTheme();
  const { user, updateUser } = useUser();
  const { selectedChatbot } = useSelectedChatbot();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    instructionsSize: 75000,
    productName: 'One',
    notifications: false,
    scraping: false,
    chatDisabled: false,
    customPoweredBy: false
  });
  const [originalOptions, setOriginalOptions] = useState('');
  const [currentOptionsString, setCurrentOptionsString] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Validación para el tamaño de instrucciones (solo números)
  const validateInstructionsSize = (value) => {
    // Si está vacío, es válido (no hay opciones configuradas)
    if (value === '') return true;
    // Convertir a número y validar que sea un número positivo
    const numValue = parseInt(value);
    return !isNaN(numValue) && numValue > 0;
  };

  // Cargar opciones actuales
  useEffect(() => {
    const loadOptions = async () => {
      if (!selectedChatbot || user?.rol !== 'admin') {
        setError('No tienes permisos para acceder a esta página');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Obtener datos del chatbot
        const chatbotData = await fetchChatbotData(selectedChatbot);
        
        // Buscar el producto seleccionado en los productos del usuario
        const selectedProduct = user?.products?.find(p => p.code === selectedChatbot);
        
        // Valores por defecto
        const defaultOptions = {
          instructionsSize: '',
          productName: '',
          notifications: false,
          scraping: false,
          chatDisabled: false,
          customPoweredBy: false
        };
        
        if (selectedProduct?.options) {
          setOriginalOptions(selectedProduct.options);
          
          // Parsear las opciones
          const instructionsSizeMatch = selectedProduct.options.match(/INSTRUCTIONS_SIZE=(\d+)/i);
          const productNameMatch = selectedProduct.options.match(/PRODUCT_NAME=([^;]+)/i);
          const notificationsMatch = selectedProduct.options.match(/NOTIFICATIONS=(\d+|yes)/i);
          const scrapingMatch = selectedProduct.options.match(/SCRAPING=(\d+|yes)/i);
          const chatDisabledMatch = selectedProduct.options.match(/CHAT_DISABLED=(\d+|yes)/i);
          const customPoweredByMatch = selectedProduct.options.match(/CUSTOM_POWERED_BY=(\d+|yes)/i);
          
          const newOptions = {
            instructionsSize: instructionsSizeMatch ? parseInt(instructionsSizeMatch[1]) : 75000,
            productName: productNameMatch ? productNameMatch[1].trim() : 'One',
            notifications: notificationsMatch ? (notificationsMatch[1] === '1' || notificationsMatch[1].toLowerCase() === 'yes') : false,
            scraping: scrapingMatch ? (scrapingMatch[1] === '1' || scrapingMatch[1].toLowerCase() === 'yes') : false,
            chatDisabled: chatDisabledMatch ? (chatDisabledMatch[1] === '1' || chatDisabledMatch[1].toLowerCase() === 'yes') : false,
            customPoweredBy: customPoweredByMatch ? (customPoweredByMatch[1] === '1' || customPoweredByMatch[1].toLowerCase() === 'yes') : false
          };
          
          setOptions(newOptions);
          updateOptionsString(newOptions);
        } else {
          // Si no tiene opciones, limpiar los datos
          setOriginalOptions('');
          setOptions(defaultOptions);
          updateOptionsString(defaultOptions);
        }
      } catch (err) {
        console.error('Error loading chatbot options:', err);
        setError('Error al cargar las opciones del chatbot');
      } finally {
        setIsLoading(false);
      }
    };

    loadOptions();
  }, [selectedChatbot, user]);

  // Actualizar la cadena de opciones cuando cambian los valores
  const updateOptionsString = (newOptions) => {
    const optionsArray = [
      `INSTRUCTIONS_SIZE=${newOptions.instructionsSize}`,
      `PRODUCT_NAME=${newOptions.productName}`,
      `NOTIFICATIONS=${newOptions.notifications ? '1' : '0'}`,
      `SCRAPING=${newOptions.scraping ? '1' : '0'}`,
      `CHAT_DISABLED=${newOptions.chatDisabled ? '1' : '0'}`,
      `CUSTOM_POWERED_BY=${newOptions.customPoweredBy ? '1' : '0'}`
    ];
    
    const optionsString = optionsArray.join(';');
    setCurrentOptionsString(optionsString);
    
    // Verificar si ha habido cambios
    setIsModified(optionsString !== originalOptions);
  };

  // Manejar cambios en las opciones
  const handleOptionChange = (option, value) => {
    const newOptions = { ...options, [option]: value };
    setOptions(newOptions);
    updateOptionsString(newOptions);
  };

  // Manejar guardar cambios
  const handleSaveChanges = async () => {
    if (!selectedChatbot || !isModified) return;
    
    setIsSaving(true);
    
    try {
      // Llamar a la API para guardar las opciones
      await saveChatbotOptions(selectedChatbot, currentOptionsString);
      
      // Actualizar el estado
      setOriginalOptions(currentOptionsString);
      setIsModified(false);
      
      // Actualizar el contexto del usuario con las nuevas opciones
      const userData = JSON.parse(sessionStorage.getItem('user'));
      if (userData && userData.products) {
        // Encontrar el producto actual y actualizar sus opciones
        const updatedProducts = userData.products.map(product => {
          if (product.code === selectedChatbot) {
            return { ...product, options: currentOptionsString };
          }
          return product;
        });
        
        // Actualizar el usuario en sessionStorage y en el contexto
        const updatedUserData = { ...userData, products: updatedProducts };
        sessionStorage.setItem('user', JSON.stringify(updatedUserData));
        updateUser(updatedUserData);
      }
      
      // Mostrar mensaje de éxito
      setSnackbar({
        open: true,
        message: 'Opciones guardadas correctamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al guardar las opciones:', error);
      // Mostrar mensaje de error
      setSnackbar({
        open: true,
        message: 'Error al guardar las opciones',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Si el usuario no es admin, mostrar mensaje de error
  if (user?.rol !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No tienes permisos para acceder a esta página. Esta página es solo para administradores.
        </Alert>
      </Box>
    );
  }

  return (
  <Container maxWidth={false}>
    <Box sx={{ p: 0 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2.5, sm: 3 }, 
          mb: { xs: 2.5, sm: 3.5 }, 
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        <Typography variant="h3" component="h1" sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                    Opciones de administrador
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
Configura las opciones avanzadas para el chatbot seleccionado.        </Typography>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mt: 2,
            borderRadius: '8px'
          }}
        >
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            Opciones para: {(() => {
              // Buscar el nombre del chatbot seleccionado
              const selectedAssistant = user?.products?.find(p => p.code === selectedChatbot);
              return selectedAssistant?.name || selectedChatbot;
            })()}
          </Typography>
          
          {originalOptions && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.text.secondary }}>
                Cadena de opciones original:
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={originalOptions}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ backgroundColor: theme.palette.action.hover }}
              />
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Tamaño máximo de instrucciones"
              variant="outlined"
              value={options.instructionsSize}
              onChange={(e) => {
                const value = e.target.value;
                // Solo permitir números
                if (value === '' || /^\d+$/.test(value)) {
                  handleOptionChange('instructionsSize', value === '' ? '' : parseInt(value));
                }
              }}
              error={options.instructionsSize !== '' && !validateInstructionsSize(options.instructionsSize)}
              helperText={options.instructionsSize !== '' && !validateInstructionsSize(options.instructionsSize) ? 'Ingrese un número válido mayor que 0' : ''}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: <Typography variant="caption" color="text.secondary">caracteres</Typography>,
              }}
            />

            <TextField
              fullWidth
              label="Nombre del producto"
              variant="outlined"
              value={options.productName}
              onChange={(e) => handleOptionChange('productName', e.target.value)}
              sx={{ mb: 3 }}
              placeholder="Ej: One, Plus, PRO"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={options.notifications}
                  onChange={(e) => handleOptionChange('notifications', e.target.checked)}
                />
              }
              label="Habilitar notificaciones"
              sx={{ mb: 2, display: 'block' }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={options.scraping}
                  onChange={(e) => handleOptionChange('scraping', e.target.checked)}
                />
              }
              label="Habilitar scraping"
              sx={{ mb: 2, display: 'block' }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={!options.chatDisabled}
                  onChange={(e) => handleOptionChange('chatDisabled', !e.target.checked)}
                />
              }
              label="Habilitar chat"
              sx={{ mb: 2, display: 'block' }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={options.customPoweredBy}
                  onChange={(e) => handleOptionChange('customPoweredBy', e.target.checked)}
                />
              }
              label="Permitir personalización del 'Powered by'"
              sx={{ mb: 3, display: 'block' }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Cadena de opciones resultante:
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={currentOptionsString}
              InputProps={{
                readOnly: true,
              }}
              sx={{ mb: 3, backgroundColor: theme.palette.action.hover }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, minHeight: '40px' }}>
              {isModified && (
                <Button
                  variant="contained"
                  disabled={isSaving}
                  onClick={handleSaveChanges}
                  startIcon={isSaving ? undefined : <SaveIcon sx={{ color: theme.palette.primary.contrastText }} />}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      borderColor: theme.palette.primary.dark,
                    },
                    height: '40px',
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    width: { xs: '100%', sm: 'auto' },
                    flex: { xs: '1 1 auto', sm: '0 0 auto' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    },
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                  }}
                >
                  {isSaving ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}
      {/* Snackbar para mensajes */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Container>
  );
}

export default AdminOptions;
